import { SiteRateSchedule } from '~hooks/useSiteRateSchedule/models';

/**
 * The dimensions that decide whether a rate card matches a job, as chips, so a dispatcher
 * can see at a glance WHY a card applied.
 *
 * Each chip carries a semantic tone rather than a colour: the caller resolves the tone
 * against `theme.semantic.*` so the chips stay legible in dark mode. The rate-card editor
 * (`FreightRateItem`) still uses its own fixed pastels — unifying the two means changing the
 * editor's appearance, which is a deliberate design decision rather than a refactor.
 */

/** Semantic tone per condition dimension; resolved to a background by the caller. */
export type RateCardConditionTone = 'info' | 'success' | 'warning';

interface RateCardConditionChip {
  key: string;
  label: string;
  tone: RateCardConditionTone;
}

/**
 * Chips for whichever match conditions this card sets.
 *
 * Pickup and dropoff collapse into a single route chip when both are present, matching how
 * the rate-card editor presents a route.
 */
export function buildRateCardConditionChips(rateCard: SiteRateSchedule) {
  const chips: RateCardConditionChip[] = [];
  const pickupSiteName = rateCard.pickupSite?.name;
  const dropoffSiteName = rateCard.dropoffSite?.name;

  if (pickupSiteName && dropoffSiteName) {
    chips.push({
      key: 'route',
      label: `${pickupSiteName} → ${dropoffSiteName}`,
      tone: 'info',
    });
  } else if (pickupSiteName || dropoffSiteName) {
    chips.push({
      key: pickupSiteName ? 'pickupSite' : 'dropoffSite',
      label: pickupSiteName ?? (dropoffSiteName as string),
      tone: 'info',
    });
  }

  if (rateCard.material?.name) {
    chips.push({ key: 'material', label: rateCard.material.name, tone: 'success' });
  }

  if (rateCard.equipmentType?.name) {
    chips.push({
      key: 'equipmentType',
      label: rateCard.equipmentType.name,
      tone: 'warning',
    });
  }

  return chips;
}
